import _ from 'lodash';
import {
  IEventRegistrationDetails,
  IEventUserRegistrationStepSummary,
  TRegistrationStepOptionLabel
} from '../../../../types/eventTypes';
import {
  TEventRegistrationFormState,
  TEventRegistrationStepFormItem,
  TEventRegistrationStepFormItemValue,
  TEventRegistrationStepFormItemData
} from '../store/details/types';
import {
  IEventBracketMatchDetails,
  IEventBracketMatchGamePVEDetails,
  IPublishedBracketSummary
} from '../../../../types/eventBracketTypes';
import { TDungeonSummary } from '../../../../types/raidTypes';
import { asset } from '../../../services/assetResolverService';
import { DYNAMIC_BRACKET_EVENT_START } from '../../../../gameConfig/mythicPlus';

export const setEventUserRegistrationSteps = (eventRegistrationDetails: IEventRegistrationDetails) => {
  return eventRegistrationDetails.registrationSteps.map((registrationStep: IEventUserRegistrationStepSummary) => {
    return {
      ...registrationStep,
      data: registrationStep.options.reduce<TEventRegistrationStepFormItemData>((accumulator, option) => {
        if (!registrationStep.preload || !registrationStep.preload[option.label]) {
          accumulator[option.label] = {
            mode: option.mode,
            value: '',
            errors: []
          };

          return accumulator;
        }

        accumulator[option.label] = {
          mode: option.mode,
          value: registrationStep.preload[option.label],
          errors: []
        };

        return accumulator;
      }, {} as TEventRegistrationStepFormItemData)
    };
  });
};

type TGetInitialEventUserRegistrationStepParams = {
  eventRegistrationStepId: number;
  eventRegistrationDetails: IEventRegistrationDetails;
};

export const getPreloadedEventUserRegistrationStep = (
  params: TGetInitialEventUserRegistrationStepParams
): TEventRegistrationStepFormItem | null => {
  const { eventRegistrationStepId, eventRegistrationDetails } = params;

  const foundRegistrationStep: IEventUserRegistrationStepSummary | undefined = _.find(
    eventRegistrationDetails.registrationSteps,
    {
      id: eventRegistrationStepId
    }
  );

  if (!foundRegistrationStep) {
    return null;
  }

  return {
    ...foundRegistrationStep,
    data: foundRegistrationStep.options.reduce<TEventRegistrationStepFormItemData>((accumulator, option) => {
      if (!foundRegistrationStep.preload || !foundRegistrationStep.preload[option.label]) {
        accumulator[option.label] = {
          mode: option.mode,
          value: '',
          errors: []
        };

        return accumulator;
      }

      accumulator[option.label] = {
        mode: option.mode,
        value: foundRegistrationStep.preload[option.label],
        errors: []
      };

      return accumulator;
    }, {} as TEventRegistrationStepFormItemData)
  };
};

type TGetEventUserRegistrationStepParams = {
  eventRegistrationStepId: number;
  eventRegistrationForm: TEventRegistrationFormState;
};

export const getEventUserRegistrationStep = (
  params: TGetEventUserRegistrationStepParams
): TEventRegistrationStepFormItem | null => {
  const { eventRegistrationStepId, eventRegistrationForm } = params;

  const foundRegistrationStep: TEventRegistrationStepFormItem | undefined = _.find(
    eventRegistrationForm.registrationSteps,
    {
      id: eventRegistrationStepId
    }
  );

  return foundRegistrationStep || null;
};

type TSetEventUserRegistrationSelectTeamStepUpdateData = Partial<
  Pick<TEventRegistrationStepFormItem, 'preload' | 'validators' | 'data'>
>;

type TSetEventUserRegistrationSelectTeamStepParams = {
  eventRegistrationStepId: number;
  eventRegistrationForm: TEventRegistrationFormState;
  updateData: TSetEventUserRegistrationSelectTeamStepUpdateData;
};

export const updateEventUserRegistrationStep = (params: TSetEventUserRegistrationSelectTeamStepParams) => {
  const { eventRegistrationStepId, eventRegistrationForm, updateData } = params;

  return eventRegistrationForm.registrationSteps.map((registrationStep: TEventRegistrationStepFormItem) => {
    if (eventRegistrationStepId === registrationStep.id) {
      return {
        ...registrationStep,
        ...updateData
      };
    }

    return registrationStep;
  });
};

export const updateRegistrationEventDataByLabel = (
  data: TEventRegistrationStepFormItemData,
  label: TRegistrationStepOptionLabel,
  updateData: Partial<Omit<TEventRegistrationStepFormItemValue, 'mode'>>
) => {
  return {
    ...data,
    [label]: {
      ...data[label],
      ...(updateData.hasOwnProperty('value') ? { value: updateData.value } : {}),
      ...(updateData.hasOwnProperty('errors') ? { errors: updateData.errors } : {})
    }
  };
};

export const getMatchBansArray = (match: IEventBracketMatchDetails, team: 'first' | 'second'): TDungeonSummary[] =>
  (match.games
    ?.map<TDungeonSummary | null>((game) => {
      const details = game.details as IEventBracketMatchGamePVEDetails;
      return team === 'first' ? details.firstTeamBan : details.secondTeamBan;
    })
    .filter(Boolean) as TDungeonSummary[]) || [];

type Split = [number, number];

export const getSplitArray = (
  details: IEventBracketMatchGamePVEDetails,
  bossCount: number,
  isRelativeSplitTimes?: boolean
): Split[] => {
  const baseSplits = [
    [details.firstTeamSplit1, details.secondTeamSplit1],
    [details.firstTeamSplit2, details.secondTeamSplit2],
    [details.firstTeamSplit3, details.secondTeamSplit3],
    [details.firstTeamSplit4, details.secondTeamSplit4],
    [details.firstTeamSplit5, details.secondTeamSplit5]
  ];

  const splits = baseSplits.map<Split>(([left, right]) => [parseTimer(left), parseTimer(right)]).slice(0, bossCount);

  // are the split times relative amounts and not total times?
  if (!isRelativeSplitTimes) {
    let index = splits.length - 1;
    while (index > 0) {
      if (splits[index][0] > 0) {
        splits[index][0] -= splits[index - 1][0];
      }
      if (splits[index][1] > 0) {
        splits[index][1] -= splits[index - 1][1];
      }
      index--;
    }
  }

  return splits;
};

export const getIsEstimatedTimes = (normalizedSplits: Split[]) =>
  normalizedSplits.reduce(
    ([left, right], [leftSplit, rightSplit]) => [left || leftSplit === 0, right || rightSplit === 0],
    [false, false]
  );

export const calculateTotalSplitTimes = (splits: Split[], untilIndex?: number): Split => {
  const cumulativeTimes: Split = [0, 0];
  const splitsToCalculate = typeof untilIndex === 'number' ? splits.slice(0, untilIndex + 1) : splits;

  splitsToCalculate.forEach((split) => {
    cumulativeTimes[0] += split[0];
    cumulativeTimes[1] += split[1];
  });

  return cumulativeTimes;
};

function parseTimer(str: string | null) {
  if (str === null) {
    return 0;
  }
  const parts = str.split(':').map((s: string) => parseInt(s, 10));
  if (parts.length === 3) {
    return parts[0] * 60 * 60 + parts[1] * 60 + parts[2];
  } else if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }

  return 0;
}

type Platoon = {
  slug: string;
  displayName: string;
  eliminated?: boolean;
  logoUrl?: string;
};

const platoonPlaceholderSlugsByGroup: { [key: string]: Platoon[] } = {
  'afdl-spring-clash': [
    { slug: 'coast-guard-gaming', displayName: 'Coast Guard Gaming' },
    { slug: 'warrior-gmr', displayName: 'Warrior GMR' },
    { slug: 'ukrainian-wow', displayName: 'Ukrainian Wow' },
    { slug: 'us-army-esports', displayName: 'US Army Esports' }
  ],
  'mythic-plus-charity-pro-am-2024': [
    { slug: 'bigger-better-balder', displayName: 'Bigger Better Balder' },
    { slug: 'danger-floofs', displayName: 'Danger Floofs' },
    { slug: 'blaster-bunnies', displayName: 'Blaster Bunnies' },
    { slug: 'porcupine-pitstop', displayName: 'Porcupine Pitstop' },
    { slug: 'big-city-slickers', displayName: 'Big City Slickers' },
    { slug: 'aps-last-hope', displayName: 'Aps Last Hope' }
  ],
  'eve-ascension-4': [
    { slug: 'five-g', displayName: 'Five G' },
    { slug: 'team-alice', displayName: 'team alice' },
    { slug: 'lets-fly', displayName: 'Lets Fly' },
    { slug: 'tracers-tops', displayName: 'Tracers Tops' }
  ],
  'tgp-dragonflight-season-4-group-a': [
    { slug: 'mandatory', displayName: 'Mandatory' },
    { slug: 'pedros-per-hour', displayName: 'Pedros Per Hour' },
    { slug: 'madge', displayName: 'Madge' },
    { slug: 'shinra-tensei', displayName: 'Shinra Tensei' },
    { slug: 'perplexed', displayName: 'Perplexed' },
    { slug: 'exposed', displayName: 'Exposed' }
  ],
  'tgp-dragonflight-season-4-group-b': [
    { slug: 'missed-count', displayName: 'Missed Count' },
    { slug: 'echo', displayName: 'Echo' },
    { slug: 'hope', displayName: 'Hope' },
    { slug: 'dratnos-guild-team', displayName: 'Dratnos Guild Team' },
    { slug: 'jif', displayName: 'JIF' },
    { slug: 'ducks-can-fly', displayName: 'Ducks Can Fly' }
  ],
  'ukrainian-tww-season-1': [
    { slug: 'kycb-team', displayName: 'KYCb TEAM' },
    { slug: 'depletto-machiatto', displayName: 'Depletto Machiatto' },
    { slug: 'nobody', displayName: 'Nobody' },
    { slug: 'dodgers', displayName: 'DODGERS' }
  ],
  'group-a-day-one': [
    {
      slug: 'mandatory',
      displayName: 'Mandatory',
      logoUrl: 'https://u-wowretail.raiderio.net/29e1/2025/01/30/Svy71yjICAP2DH7cU5CKK8P7Sa/image.png'
    },
    {
      slug: 'tbd',
      displayName: 'TBD',
      logoUrl: 'https://u-wowretail.raiderio.net/dd25/2025/01/30/0H4rYsMP9SovIVP7hVDlZoVmh7/image.png'
    },
    {
      slug: 'better-call-sosal',
      displayName: 'Better Call SoSaL',
      logoUrl: 'https://u-wowretail.raiderio.net/c1a6/2025/01/30/5he7WbMffMqAZvZBaFpbRKHhMl/image.png'
    },
    {
      slug: 'firmawand',
      displayName: 'Firmawand',
      logoUrl: 'https://u-wowretail.raiderio.net/3c74/2025/01/30/NSiZDd5rRuYFTQCFle8vdy7Pgz/image.png'
    }
  ],
  'group-a-day-two': [
    {
      slug: 'no-speaky',
      displayName: 'No Speaky',
      logoUrl: 'https://u-wowretail.raiderio.net/c441/2025/01/30/95MpEUnWS6GuFyalRiBlCmnPKi/image.png'
    },
    {
      slug: 'shiny-unicorns',
      displayName: 'Shiny Unicorns',
      logoUrl: 'https://u-wowretail.raiderio.net/d605/2025/01/30/p8VuytkQ5jtmeZqK8EfJLJpMsz/image.png'
    },
    {
      slug: 'los-golfos',
      displayName: 'Los Golfos',
      logoUrl: 'https://u-wowretail.raiderio.net/5049/2025/01/30/ek4G7hr0NbG1TvWRhfl2y98DYS/image.png'
    },
    {
      slug: 'randomize',
      displayName: 'Randomize',
      logoUrl: 'https://u-wowretail.raiderio.net/351b/2025/01/30/q4si3Dxi695ElyK6dhNVQZSRT4/image.png'
    }
  ],
  'group-a-day-three': [
    {
      slug: 'mandatory',
      displayName: 'Mandatory',
      logoUrl: 'https://u-wowretail.raiderio.net/29e1/2025/01/30/Svy71yjICAP2DH7cU5CKK8P7Sa/image.png'
    },
    {
      slug: 'firmawand',
      displayName: 'Firmawand',
      logoUrl: 'https://u-wowretail.raiderio.net/3c74/2025/01/30/NSiZDd5rRuYFTQCFle8vdy7Pgz/image.png'
    },
    {
      slug: 'no-speaky',
      displayName: 'No Speaky',
      logoUrl: 'https://u-wowretail.raiderio.net/c441/2025/01/30/95MpEUnWS6GuFyalRiBlCmnPKi/image.png'
    },
    {
      slug: 'shiny-unicorns',
      displayName: 'Shiny Unicorns',
      logoUrl: 'https://u-wowretail.raiderio.net/d605/2025/01/30/p8VuytkQ5jtmeZqK8EfJLJpMsz/image.png'
    }
  ],
  'group-b-day-one': [
    {
      slug: 'streamerzonegg',
      displayName: 'Streamerzonegg',
      logoUrl: 'https://u-wowretail.raiderio.net/f2c4/2025/02/07/wQznsWxL8eTW41iDRb7dLdMY1b/image.png'
    },
    {
      slug: 'polish-mountain',
      displayName: 'Polish Mountain',
      logoUrl: 'https://u-wowretail.raiderio.net/4628/2024/11/01/BRvrp62hRYtYP2IvdXpFzeJaEV/image.png'
    },
    {
      slug: 'baguettes',
      displayName: 'Baguettes',
      logoUrl: 'https://u-wowretail.raiderio.net/f7cc/2025/02/07/9Rl0tiuY7D75NwnV3G3YjWN9Ry/image.png'
    },
    {
      slug: 'fikinites',
      displayName: 'Fikinites',
      logoUrl: 'https://u-wowretail.raiderio.net/da84/2025/02/07/ExuxILEq5to44bh5sxTcLUYfte/image.png'
    }
  ],
  'group-b-day-two': [
    {
      slug: 'esc-gaming',
      displayName: 'ESC Gaming',
      logoUrl: 'https://u-wowretail.raiderio.net/8a9e/2025/02/07/e7IalhanUbemRSfgEoaljFqlrv/image.png'
    },
    {
      slug: 'cinema',
      displayName: 'Cinema',
      logoUrl: 'https://u-wowretail.raiderio.net/d8c8/2025/02/07/8q6IZHgdS5rca2ZKPVtW1IJfXq/image.png'
    },
    {
      slug: 'nilbasacademy',
      displayName: 'NilbasAcademy',
      logoUrl: 'https://u-wowretail.raiderio.net/f27a/2025/02/07/NdZizZ5j4Si49v6FsrwUXEegCL/image.png'
    },
    {
      slug: 'lindormi-fan-club',
      displayName: 'Lindormi Fan Club',
      logoUrl: 'https://u-wowretail.raiderio.net/71a3/2025/02/07/CljT4Lmbhj4iUqlrCceuyqwxqW/image.png'
    }
  ],
  'group-b-day-three': [
    {
      slug: 'streamerzonegg',
      displayName: 'Streamerzonegg',
      logoUrl: 'https://u-wowretail.raiderio.net/f2c4/2025/02/07/wQznsWxL8eTW41iDRb7dLdMY1b/image.png'
    },
    {
      slug: 'fikinites',
      displayName: 'Fikinites',
      logoUrl: 'https://u-wowretail.raiderio.net/da84/2025/02/07/ExuxILEq5to44bh5sxTcLUYfte/image.png'
    },
    {
      slug: 'cinema',
      displayName: 'Cinema',
      logoUrl: 'https://u-wowretail.raiderio.net/d8c8/2025/02/07/8q6IZHgdS5rca2ZKPVtW1IJfXq/image.png'
    },
    {
      slug: 'esc-gaming',
      displayName: 'ESC Gaming',
      logoUrl: 'https://u-wowretail.raiderio.net/8a9e/2025/02/07/e7IalhanUbemRSfgEoaljFqlrv/image.png'
    }
  ],
  'global-finals': [
    {
      slug: 'mandatory',
      displayName: 'Mandatory',
      logoUrl: 'https://u-wowretail.raiderio.net/29e1/2025/01/30/Svy71yjICAP2DH7cU5CKK8P7Sa/image.png'
    },
    {
      slug: 'no-speaky',
      displayName: 'No Speaky',
      logoUrl: 'https://u-wowretail.raiderio.net/c441/2025/01/30/95MpEUnWS6GuFyalRiBlCmnPKi/image.png'
    },
    {
      slug: 'streamerzonegg',
      displayName: 'Streamerzonegg',
      logoUrl: 'https://u-wowretail.raiderio.net/f2c4/2025/02/07/wQznsWxL8eTW41iDRb7dLdMY1b/image.png'
    },
    {
      slug: 'esc-gaming',
      displayName: 'ESC Gaming',
      logoUrl: 'https://u-wowretail.raiderio.net/8a9e/2025/02/07/e7IalhanUbemRSfgEoaljFqlrv/image.png'
    },
    {
      slug: 'stamp',
      displayName: 'Stamp',
      logoUrl: 'https://u-wowretail.raiderio.net/faa6/2025/02/14/IACMMw7Teej3goUlSslNe0kCsQ/image.png'
    },
    {
      slug: 'phoenix-gang-media',
      displayName: 'Phoenix Gang Media',
      logoUrl: 'https://u-wowretail.raiderio.net/2ee7/2025/02/10/84HaLpu7yLtCzg2NV60QvZ3B5p/image.png'
    }
  ]
};

export function getPlatoonSlugsByGroupQueryString(group: string, logoFilePath?: string): string {
  let qString = '';
  const teams: Platoon[] = platoonPlaceholderSlugsByGroup[group];
  const teamsToKo: string[] = [];

  for (const platoon of teams) {
    const imagePath = platoon.logoUrl ? platoon.logoUrl : asset(`${logoFilePath}${platoon.slug}.png`);
    qString = qString + `&teamName_${platoon.slug}=${platoon.displayName}` + `&teamLogo_${platoon.slug}=${imagePath}`;

    if (platoon.eliminated) {
      teamsToKo.push(platoon.slug);
    }
  }

  return qString + `&teamsToKo=${teamsToKo.join(',')}`;
}

export type TEventsLeaderboardWidgetOptions = {
  skipTotalDungeonTime: boolean;
  skipTotal: boolean;
  skipLogo: boolean;
  usesBestDungeon: boolean;
};

export const getEventsLeaderboardWidgetQueryParams = ({
  bracketDetails,
  eventSlug,
  options,
  showEmptyTeams = false
}: {
  bracketDetails: IPublishedBracketSummary;
  eventSlug: string;
  options?: TEventsLeaderboardWidgetOptions;
  showEmptyTeams?: boolean;
}) => {
  const validDungeons = bracketDetails.dungeons.map((dungeon) => {
    return dungeon.dungeon?.slug;
  });

  const dungeonsCsv = validDungeons.join(',');

  // THIS IS A HACK FOR AFDL TO GET PLACEHOLDERS ON THE LEADERBOARD IDEALLY IT IS DATA DRIVEN FROM THE SECTION.
  let placeholders = '';
  let skipLogos = false;

  if (eventSlug === 'afdl-spring-clash') {
    placeholders = `&${getPlatoonSlugsByGroupQueryString(
      'afdl-spring-clash',
      '/images/events/expansion9/afdl-spring-clash/logos/'
    )}`;
  } else if (eventSlug === 'mythic-plus-charity-pro-am-2024') {
    placeholders = `&${getPlatoonSlugsByGroupQueryString(
      'mythic-plus-charity-pro-am-2024',
      '/images/charity-pro-am/2024/team-logos/'
    )}`;
  } else if (eventSlug === 'eve-ascension-4') {
    placeholders = `&${getPlatoonSlugsByGroupQueryString(
      'eve-ascension-4',
      '/images/events/expansion9/eve-ascension-2024/teams/'
    )}`;
  } else if (eventSlug === 'tgp-dragonflight-season-4' && bracketDetails.slug === 'group-a') {
    placeholders = `&${getPlatoonSlugsByGroupQueryString(
      'tgp-dragonflight-season-4-group-a',
      '/images/events/expansion9/season-df-4-tgp-awc/team-logos/'
    )}`;
  } else if (eventSlug === 'tgp-dragonflight-season-4' && bracketDetails.slug === 'group-b') {
    placeholders = `&${getPlatoonSlugsByGroupQueryString(
      'tgp-dragonflight-season-4-group-b',
      '/images/events/expansion9/season-df-4-tgp-awc/team-logos/'
    )}`;
  } else if (
    eventSlug === 'ukrainian-mythic-tournament-tww-season-1' &&
    bracketDetails.slug === 'ukrainian-tww-season-1'
  ) {
    placeholders = `&${getPlatoonSlugsByGroupQueryString('ukrainian-tww-season-1', '/images/events/expansion10/ukraine-season-1/team-logos/')}`;
  } else if (eventSlug === 'tgp-the-war-within-season-1' && bracketDetails.slug === 'group-a-day-one') {
    placeholders = `&${getPlatoonSlugsByGroupQueryString('group-a-day-one')}`;
  } else if (eventSlug === 'tgp-the-war-within-season-1' && bracketDetails.slug === 'group-a-day-two') {
    placeholders = `&${getPlatoonSlugsByGroupQueryString('group-a-day-two')}`;
  } else if (eventSlug === 'tgp-the-war-within-season-1' && bracketDetails.slug === 'group-a-day-three') {
    placeholders = `&${getPlatoonSlugsByGroupQueryString('group-a-day-three')}`;
  } else if (eventSlug === 'tgp-the-war-within-season-1' && bracketDetails.slug === 'group-b-day-one') {
    placeholders = `&${getPlatoonSlugsByGroupQueryString('group-b-day-one')}`;
  } else if (eventSlug === 'tgp-the-war-within-season-1' && bracketDetails.slug === 'group-b-day-two') {
    placeholders = `&${getPlatoonSlugsByGroupQueryString('group-b-day-two')}`;
  } else if (eventSlug === 'tgp-the-war-within-season-1' && bracketDetails.slug === 'group-b-day-three') {
    placeholders = `&${getPlatoonSlugsByGroupQueryString('group-b-day-three')}`;
  } else if (eventSlug === 'tgp-the-war-within-season-1' && bracketDetails.slug === 'global-finals') {
    placeholders = `&${getPlatoonSlugsByGroupQueryString('global-finals')}`;
  }

  let widgetUrl = `?dungeons=${dungeonsCsv}&numDungeons=${
    bracketDetails.rulesetMaxScoredDungeons || bracketDetails.dungeons.length
  }&eventId=${bracketDetails.id + DYNAMIC_BRACKET_EVENT_START}&season=${
    bracketDetails.rulesetSeasonSlug
  }${placeholders}`;

  if (options) {
    if (options.skipTotalDungeonTime) {
      widgetUrl += '&skipTotalDungeonTime=true';
    }

    if (options.skipTotal) {
      widgetUrl += '&skipTotal=true';
    }

    if (options.skipLogo) {
      widgetUrl += '&skipLogo=true';
    }

    if (options.usesBestDungeon) {
      widgetUrl += '&usesBestDungeon=true';
    }
  } else {
    widgetUrl += `&skipTotalDungeonTime=true&skipTotal=true&skipLogo=${skipLogos}`;
  }

  if (showEmptyTeams) {
    widgetUrl += '&showEmptyTeams=true';
  }

  return widgetUrl;
};
